import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Lock, ArrowLeft, Shield, Eye, Database, Globe, Mail } from "lucide-react";
import { Footer } from "@/components/Footer";
import { Navbar } from "@/components/Navbar";

export default function Privacy() {
  const { t } = useTranslation();
  const lastUpdated = "January 1, 2026";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <Navbar />

      <div className="container flex justify-between items-center h-12">
        <Link href="/">
          <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </a>
        </Link>
      </div>

      <main className="flex-1 container py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-4">{t('footer.privacy')}</h1>
          <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
        </div>

        {/* Quick Summary */}
        <section className="card-elegant p-8 mb-8 border-primary/30">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Privacy at a Glance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Eye className="w-8 h-8 text-primary mb-2" />
              <h3 className="font-medium mb-1">What We Collect</h3>
              <p className="text-sm text-muted-foreground">
                Account info, listings, messages, and usage data to provide our service.
              </p>
            </div>
            <div>
              <Database className="w-8 h-8 text-primary mb-2" />
              <h3 className="font-medium mb-1">How We Use It</h3>
              <p className="text-sm text-muted-foreground">
                To operate the platform, improve our service, and keep you safe.
              </p>
            </div>
            <div>
              <Globe className="w-8 h-8 text-primary mb-2" />
              <h3 className="font-medium mb-1">Your Rights</h3>
              <p className="text-sm text-muted-foreground">
                Access, correct, delete your data, or opt out of marketing anytime.
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground">
              At Listo Market ("we," "us," or "our"), we are committed to protecting your privacy. This 
              Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
              you use our website and mobile application (collectively, the "Service").
            </p>
            <p className="text-muted-foreground mt-4">
              Please read this policy carefully. By using the Service, you consent to the practices 
              described in this Privacy Policy.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium mb-2 mt-6">Personal Information</h3>
            <p className="text-muted-foreground mb-4">
              When you create an account, we collect:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Name and email address</li>
              <li>Phone number (optional)</li>
              <li>Profile photo (optional)</li>
              <li>Location information (city, country)</li>
              <li>Payment information for premium features</li>
            </ul>

            <h3 className="text-lg font-medium mb-2 mt-6">Listing Information</h3>
            <p className="text-muted-foreground mb-4">
              When you post a listing, we collect:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Title, description, and price</li>
              <li>Photos and images</li>
              <li>Category and location</li>
              <li>Contact preferences</li>
            </ul>

            <h3 className="text-lg font-medium mb-2 mt-6">Automatically Collected Information</h3>
            <p className="text-muted-foreground mb-4">
              We automatically collect:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Device information (type, OS, browser)</li>
              <li>IP address and approximate location</li>
              <li>Usage patterns and preferences</li>
              <li>Cookies and similar technologies</li>
            </ul>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground mb-4">
              We use your information to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Send you technical notices and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Detect and prevent fraud and abuse</li>
              <li>Enforce our Terms of Service</li>
              <li>Personalize your experience</li>
              <li>Send marketing communications (with your consent)</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">4. Information Sharing</h2>
            <p className="text-muted-foreground mb-4">
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Other Users:</strong> Your public profile and listings are visible to others</li>
              <li><strong>Service Providers:</strong> Companies that help us operate our business</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect rights</li>
              <li><strong>Business Transfers:</strong> In connection with mergers or acquisitions</li>
              <li><strong>With Your Consent:</strong> For any other purpose you authorize</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
            <p className="text-muted-foreground">
              We implement appropriate technical and organizational measures to protect your personal 
              information, including encryption, secure servers, and regular security assessments. 
              However, no method of transmission over the Internet is 100% secure, and we cannot 
              guarantee absolute security.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">6. Data Retention</h2>
            <p className="text-muted-foreground">
              We retain your information for as long as necessary to provide our Service and fulfill 
              the purposes outlined in this policy. We may retain certain information for legitimate 
              business purposes or as required by law. When you delete your account, we will delete 
              or anonymize your personal information within 30 days, except where retention is required.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">7. Your Rights</h2>
            <p className="text-muted-foreground mb-4">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Objection:</strong> Object to processing of your information</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              To exercise these rights, please contact us at privacy@listomarket.app.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">8. Cookies and Tracking</h2>
            <p className="text-muted-foreground mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Keep you signed in</li>
              <li>Remember your preferences</li>
              <li>Understand how you use our Service</li>
              <li>Deliver relevant advertisements</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              You can control cookies through your browser settings. Disabling cookies may affect 
              the functionality of our Service.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">9. International Transfers</h2>
            <p className="text-muted-foreground">
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place, including Standard Contractual Clauses 
              and other approved transfer mechanisms.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
            <p className="text-muted-foreground">
              Our Service is not intended for children under 18 years of age. We do not knowingly 
              collect personal information from children. If you believe we have collected information 
              from a child, please contact us immediately.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of significant 
              changes by posting a notice on our Service or sending you an email. Your continued use 
              of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="card-elegant p-8 mb-6">
            <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
            <p className="text-muted-foreground">
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <p className="text-muted-foreground mt-4">
              <strong>Email:</strong> privacy@listomarket.app<br />
              <strong>Address:</strong> 123 Market Street, San Francisco, CA 94105<br />
              <strong>Data Protection Officer:</strong> dpo@listomarket.app
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
